import type { FallbackRender } from "@sentry/react";

import { type ReactElement, useLayoutEffect, useMemo } from "react";

import { Button, LorumLogoMark, LorumWordMark, MainContent } from "@shared/design-system-kit";

import styles from "./error-message.module.css";

const FAILED_DYNAMIC_MODULE_FETCH_MSG = "Failed to fetch dynamically imported module";
const FAILED_TO_PRELOAD_CSS_MSG = "Unable to preload CSS for";
const DEFAULT_ERROR_TITLE = "Something went wrong";
const NEW_VERSION_AVAILABE_TITLE = "New version available";

type ErrorCopy = {
	message: string;
	title: string;
};

export function ErrorMessage({ error }: Parameters<FallbackRender>[0]): ReactElement {
	const errorCopy: ErrorCopy = useMemo(() => {
		if (error instanceof Error) {
			if (
				error.message.startsWith(FAILED_DYNAMIC_MODULE_FETCH_MSG) ||
				error.message.startsWith(FAILED_TO_PRELOAD_CSS_MSG)
			) {
				return {
					message:
						"A new version of the portal is available. Refresh the page to continue.",
					title: NEW_VERSION_AVAILABE_TITLE,
				};
			}
			return {
				message: error.message,
				title: DEFAULT_ERROR_TITLE,
			};
		}

		return {
			message: "An unexpected error occurred. Please try again.",
			title: DEFAULT_ERROR_TITLE,
		};
	}, [error]);

	const showButtons = errorCopy.title !== NEW_VERSION_AVAILABE_TITLE;

	useLayoutEffect(() => {
		// Each deploy replaces the assets hosted on Cloudflare, so a stale browser tab
		// throws this error when it tries to load a module that no longer exists on the server.
		// Redirecting through logout sends the user to the login page, which then fetches
		// the latest assets.
		if (errorCopy.title === NEW_VERSION_AVAILABE_TITLE) {
			window.open(`${import.meta.env.VITE_FUSE_API_BASE_URL}/auth/logout`, "_self");
		}
	}, [errorCopy.title]);

	return (
		<MainContent className={styles.errorMessage}>
			<div className={styles.logo}>
				<LorumLogoMark />
				<LorumWordMark />
			</div>
			<h1 className="title-1">{errorCopy.title}</h1>
			<p className="body-1">{errorCopy.message}</p>
			{showButtons && (
				<div className={styles.actions}>
					<Button href="/" variant="primary">
						Dashboard
					</Button>
					<Button
						onPress={() => {
							// This error is often triggered after a deploy
							// where the hash in the modules got changed.
							// To fetch the new modules a full reload is required
							window.location.reload();
						}}
						variant="secondary"
					>
						Try again
					</Button>
				</div>
			)}
		</MainContent>
	);
}
